import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BreadCrumb from "../Components/BreadCrumb";
import bgImg from "../Images/gallery-breadcrumb.webp";
import { tradeFairGallery } from "../Data";
import Cta from "../Components/Cta";
import Certificates from "../Components/Certificates";
import ContactSection from "../Components/ContactSection";
import GLightbox from "glightbox";
import "glightbox/dist/css/glightbox.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import organizationSchema from "../Schema/OrganizationSchema";
import SchemaMarkup from "../Components/SchemaMarkup";
import breadcrumbSchema from "../Schema/BreadcrumbSchema";
import aboutPageSchema from "../Schema/AboutPageSchema";

gsap.registerPlugin(ScrollTrigger);

const Trade = () => {
  useEffect(() => {
    document.title =
      "Trade Participations & Honours | Iris Impulse India Pvt. Ltd.";

    const metaDesc = document.querySelector("meta[name='description']");
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Explore Iris Impulse India Pvt. Ltd.’s global trade participations, exhibitions, and industry honours reflecting our excellence in minerals export."
      );
    }

     const canonical = document.querySelector("link[rel='canonical']");
        if (canonical) {
          canonical.setAttribute("href", "https://irisimpulse.com/about-us/trade-participations-honours");
      }

    const ogTitle = document.querySelector("meta[property='og:title']");
    if (ogTitle) {
      ogTitle.setAttribute(
        "content",
        "Trade Participations & Honours | Iris Impulse India Pvt. Ltd."
      );
    }

    const ogDesc = document.querySelector("meta[property='og:description']");
    if (ogDesc) {
      ogDesc.setAttribute(
        "content",
        "Explore Iris Impulse India Pvt. Ltd.’s global trade participations, exhibitions, and industry honours reflecting our excellence in minerals export."
      );
    }

    const ogUrl = document.querySelector("meta[property='og:url']");
      if (ogUrl) {
        ogUrl.setAttribute("content", "https://irisimpulse.com/about-us/trade-participations-honours");
      }
            
      const ogImage = document.querySelector("meta[property='og:image']");
        if (ogImage) {
          ogImage.setAttribute(
          "content",
          "https://irisimpulse.com/Images/trade-participation-honours.webp"
      );
  }

    const twitterTitle = document.querySelector("meta[name='twitter:title']");
    if (twitterTitle) {
      twitterTitle.setAttribute(
        "content",
        "Trade Participations & Honours | Iris Impulse India Pvt. Ltd."
      );
    }

    const twitterDesc = document.querySelector(
      "meta[name='twitter:description']"
    );
    if (twitterDesc) {
      twitterDesc.setAttribute(
        "content",
        "Explore Iris Impulse India Pvt. Ltd.’s global trade participations, exhibitions, and industry honours reflecting our excellence in minerals export."
      );
    }

     const twitterImage = document.querySelector("meta[name='twitter:image']");
      if (twitterImage) {
        twitterImage.setAttribute(
        "content",
        "https://irisimpulse.com/Images/trade-participation-honours.webp"
      );
              }
  }, []);

  // Safe initial tab lookup
  const initialTab =
    Array.isArray(tradeFairGallery) && tradeFairGallery.length > 0
      ? tradeFairGallery[0]?.tab || ""
      : "";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Layout refs for GSAP Pinning
  const layoutRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const lightbox = GLightbox({
      selector: ".trade-page-gallery-lightbox",
      loop: true,
      touchNavigation: true,
    });

    return () => lightbox.destroy();
  }, [activeTab]);

  // Sticky pinning ONLY for screens larger than 1200px
  // Anything 1200px and below will NOT be pinned
  useEffect(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1201px)", () => {
      ScrollTrigger.create({
        trigger: layoutRef.current,
        start: "top 100px",
        end: "bottom+=200px bottom",
        pin: sidebarRef.current,
        pinSpacing: false,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      });
    });

    return () => mm.revert();
  }, []);

  // Refresh ScrollTrigger when switching tabs
  useEffect(() => {
    const timer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 150);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);

    if (layoutRef.current) {
      const headerOffset = 100;

      let elementTop = 0;
      let el = layoutRef.current;
      while (el) {
        elementTop += el.offsetTop;
        el = el.offsetParent;
      }

      const targetY = elementTop - headerOffset;

      gsap.to(window, {
        duration: 0.6,
        scrollTo: { y: targetY, autoKill: false },
        ease: "power2.out",
        onComplete: () => {
          ScrollTrigger.refresh();
        },
      });
    }
  };

  const activeImages =
    (Array.isArray(tradeFairGallery) &&
      tradeFairGallery.find((item) => item?.tab === activeTab)?.images) ||
    [];

  if (!Array.isArray(tradeFairGallery) || tradeFairGallery.length === 0) {
    return null;
  }

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, ease: "easeOut" },
    },
  };


  const organizationData = organizationSchema();
  const breadcrumbData = breadcrumbSchema({
  items: [
    {
      name: "Home",
      url: "https://irisimpulse.com/",
    },
    {
      name: "About Us",
      url: "https://irisimpulse.com/about-us"
    },
    {
      name: "Trade Participations & Honours",
    },
  ],
});
const aboutPageData = aboutPageSchema({
  url: "https://irisimpulse.com/about-us/trade-participations-honours",
  title: "Trade Participations & Honours | Iris Impulse India Pvt. Ltd.",
  description:
    "Explore Iris Impulse India Pvt. Ltd.’s global trade participations, exhibitions, and industry honours reflecting our excellence in minerals export.",
  image: "https://irisimpulse.com/Images/trade-participation-honours.webp",
});
  return (
    <>
    <SchemaMarkup data={organizationData} />
    <SchemaMarkup data={breadcrumbData} />
    <SchemaMarkup data={aboutPageData} />
    
      <BreadCrumb title="Trade Participations & Honours" bgImage={bgImg} />

      <motion.section
        className="trade-page-gallery-section container"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        variants={staggerContainer}
      >
        <div className="trade-page-gallery-layout" ref={layoutRef}>
          {/* LEFT TABS SIDEBAR */}
          <motion.div
            className="trade-page-gallery-sidebar"
            ref={sidebarRef}
            variants={staggerContainer}
          >
            <h2 className="card-heading">Global Expos & Events</h2>

            {tradeFairGallery.map((item) => (
              <motion.button
                key={item.id || item.tab}
                variants={fadeUp}
                whileHover={{ x: 6 }}
                whileTap={{ scale: 0.96 }}
                transition={{ duration: 0.2 }}
                onClick={() => handleTabChange(item.tab)}
                className={`trade-page-gallery-tab ${
                  activeTab === item.tab ? "active" : ""
                }`}
              >
                {item.tab}
              </motion.button>
            ))}
          </motion.div>

          {/* RIGHT CONTENT */}
          <div className="trade-page-gallery-content">
            {/* MOBILE DROPDOWN */}
            <div className="trade-page-gallery-dropdown">
              <button
                className="trade-page-gallery-dropdown-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {activeTab}
                <span
                  className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}
                >
                  ▼
                </span>
              </button>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    className="trade-page-gallery-dropdown-menu"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    {tradeFairGallery.map((item) => (
                      <button
                        key={item.id || item.tab}
                        className="trade-page-gallery-dropdown-item"
                        onClick={() => {
                          setActiveTab(item.tab);
                          setDropdownOpen(false);
                        }}
                      >
                        {item.tab}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* GALLERY GRID */}
            <motion.div
              className="trade-page-gallery-grid"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {activeImages.map((item, index) => (
                <motion.div
                  key={item.image || index}
                  className="trade-page-gallery-card"
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  <a
                    href={item.image}
                    className="trade-page-gallery-lightbox"
                  >
                    <img
                      src={item.image}
                      alt={`${activeTab} ${index + 1} | Iris Impulse India Pvt. Ltd.`}
                    />
                  </a>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.section>

      <Cta />
      <Certificates />
      <ContactSection />
    </>
  );
};

export default Trade;